/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useConfigService } from '@thd-nucleus/experience-context';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import {
  arrayOf,
  bool,
  client,
  number,
  params,
  shape,
  string,
  useDataModel
} from '@thd-nucleus/data-sources';

import {
  checkScheduleAMeasureEligibility,
  isBrioProduct,
  isCustomKitchenCabinetProduct,
  isChristmasMessagingEligible
} from '../../productPodUtils';
import { Badge } from './Badge';

export const ProductBadge = ({ itemId, storeId = null }) => {

  const [badgesInState, setBadges] = useState([]);
  let christmasDeliveryMessageBuffer = useConfigService('fs:christmasDeliveryMessageBuffer') || 0;
  christmasDeliveryMessageBuffer = parseInt(christmasDeliveryMessageBuffer, 10);
  const theme = useTheme(ProductBadge);
  const {
    hideHolidayDeliveryBadge
  } = theme.props;

  const { data } = useDataModel('product', {
    variables: {
      itemId,
      storeId,
    },
    ssr: !christmasDeliveryMessageBuffer
  });

  const { product } = data || {};

  useEffect(() => {
    // product?.badges can be null from api response, so checking if itemId exist in response
    if (product?.itemId) {
      setBadges(product?.badges);
    }
  }, [product?.badges]);

  let badges = product?.badges || badgesInState;
  const isScheduleAMeasureEligible = checkScheduleAMeasureEligibility(product);

  if (isScheduleAMeasureEligible || isBrioProduct(product)) {
    const installedBadge = { name: 'installed' };
    badges = [...badges, installedBadge];
  }

  if (isCustomKitchenCabinetProduct(product)) {
    const customBadge = { name: 'custom' };
    badges = [...badges, customBadge];
  }

  if (!hideHolidayDeliveryBadge && christmasDeliveryMessageBuffer && isChristmasMessagingEligible(product, christmasDeliveryMessageBuffer)) {
    // prioritize christmas badge to be first in the list
    badges = [ { name: 'christmas'}, ...badges];
  }

  if (!badges?.length || !badges[0]) return null;

  const badgeModels = {
    new: 'New',
    seasonal: 'New This Season',
    exclusive: 'Exclusive',
    trending: 'Trending',
    bestseller: 'Best Seller',
    'top rated': 'Top Rated',
    custom: 'Custom',
    cpo: 'Custom Catalog',
    christmas: 'Delivery by Christmas',
    propick: 'Pro Pick',
  };

  const displayableBadges = new Set([...Object.keys(badgeModels), 'previously purchased']);
  const itemBadge = badges
    .filter((badge) => displayableBadges.has(badge.name))
    .map((badge) => (badge.name === 'previously purchased' ? badge.label : badgeModels[badge.name]));
  const badge = itemBadge.length ? itemBadge[0] : null;

  if (!badge) {
    return null;
  }

  return (
    <>
      <Badge text={badge} />
    </>
  );
};

ProductBadge.propTypes = {
  itemId: PropTypes.string.isRequired,
  storeId: PropTypes.string
};


const installServices = params({ storeId: string(), zipCode: string() }).shape({
  scheduleAMeasure: bool(),
});

if (installServices.skip) {
  installServices.skip('skipInstallServices', true);
}

ProductBadge.themeProps = {
  hideHolidayDeliveryBadge: PropTypes.bool,
};

ProductBadge.defaultThemeProps = {
  hideHolidayDeliveryBadge: false,
};

ProductBadge.dataModel = {
  product: params({
    itemId: string().isRequired()
  }).shape({
    itemId: string(),
    dataSources: string(),
    installServices,
    info: shape({
      isLiveGoodsProduct: bool(),
      isCustomKitchenCabinet: bool().client(),
      isBrioSku: bool().client(),
    }),
    availabilityType: shape({
      type: string()
    }),
    fulfillment: client(params({ storeId: string(), zipCode: string() }).shape({
      fulfillmentOptions: arrayOf(shape({
        services: arrayOf(shape({
          type: string(),
          locations: arrayOf(shape({
            isAnchor: bool(),
            inventory: shape({
              quantity: number()
            })
          }))
        }))
      }))
    })),
    badges: params({ storeId: string() }).arrayOf(shape({
      name: string(),
      label: string(),
    })),
  })
};

ProductBadge.displayName = 'ProductBadge';
